﻿export function downloadDocument(location, documentName) {
    return fetch(location, {
        credentials: 'same-origin',
        headers: {
            'Accept': 'application/pdf, application/octet-stream;q=0.9, */*;q=0.8',
            'X-Requested-With': 'XMLHttpRequest',
        },
    })
        .then(response => {
            if (response.redirected) {
                throw new Error('The download request was redirected before the document could be retrieved.');
            }

            if (!response.ok) {
                return createDownloadError(response).then(error => {
                    throw error;
                });
            }

            // Extract filename from Content-Disposition header
            let filename = documentName || 'download.pdf';
            const contentDisposition = response.headers.get('Content-Disposition');
            if (contentDisposition) {
                // Handle both formats: filename="file.pdf" and filename*=UTF-8''file.pdf
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                }
                // Handle RFC 5987 encoded filenames (filename*=UTF-8''name.pdf)
                const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''(.+)/i);
                if (filenameStarMatch && filenameStarMatch[1]) {
                    filename = decodeURIComponent(filenameStarMatch[1]);
                }
            }

            // Extract MIME type from Content-Type header
            let mimeType = response.headers.get('Content-Type') || 'application/pdf';
            // Remove charset if present (e.g., "application/pdf; charset=utf-8" -> "application/pdf")
            mimeType = mimeType.split(';')[0].trim().toLowerCase();

            if (!isSupportedDownloadType(mimeType)) {
                throw new Error(`Unexpected content type received: ${mimeType || 'unknown'}.`);
            }

            return response.blob().then(blob => ({ blob, filename, mimeType }));
        })
        .then(({ blob, filename }) => {
            if (!blob || blob.size === 0) {
                throw new Error('The downloaded document is empty.');
            }

            downloadBlob(blob, filename);
        })
        .catch(error => {
            console.error('Error downloading document:', error);
            throw error;
        });
}

function createDownloadError(response) {
    const fallbackMessage = getFallbackErrorMessage(response.status);
    const contentType = response.headers.get('Content-Type') || '';

    if (contentType.includes('application/json')) {
        return response.json()
            .then(payload => {
                const serverMessage = payload?.detail || payload?.message || payload?.title || payload?.redirectTo;
                return new Error(serverMessage || fallbackMessage);
            })
            .catch(() => new Error(fallbackMessage));
    }

    if (contentType.startsWith('text/')) {
        return response.text()
            .then(responseText => new Error(extractServerErrorMessage(responseText) || fallbackMessage))
            .catch(() => new Error(fallbackMessage));
    }

    return Promise.resolve(new Error(fallbackMessage));
}

function extractServerErrorMessage(responseText) {
    const trimmedText = responseText?.trim();

    if (!trimmedText) {
        return null;
    }

    if (/^<!doctype|^<html|<body/i.test(trimmedText)) {
        return null;
    }

    return trimmedText;
}

function getFallbackErrorMessage(status) {
    if (status === 401 || status === 403) {
        return 'You are no longer authorized to download this document.';
    }

    if (status === 404) {
        return 'The document is not found (404).';
    }

    return `Network response was not ok (${status}).`;
}

function isSupportedDownloadType(mimeType) {
    return mimeType === 'application/pdf' || mimeType === 'application/octet-stream';
}

function downloadBlob(data, filename) {
    const a = document.createElement("a");
    const url = URL.createObjectURL(data);

    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(function () {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    }, 0);
}